import {
  addDays,
  addMonths,
  addYears,
  eachDayOfInterval,
  eachMonthOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isValid,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from "date-fns";

/**
 * The one horizontal scale every Timeline row shares.
 *
 * The metric rows are Recharts charts and the tests/service rows are plain
 * positioned divs; both place a date with `xPct` against the same window, so
 * a reading, a test dot and a service bar on the same day line up exactly.
 *
 * The window is half-open, `[startMs, endMs)`: `endMs` is midnight after the
 * last day, so the last day gets its full width.
 */
export type TimelinePeriod = "week" | "month" | "year";

export interface TimelineTick {
  /** The middle of the tick's day (or month, in a year) — the label sits in
   *  the middle of its column. */
  t: number;
  label: string;
}

export interface TimelineWindow {
  period: TimelinePeriod;
  startMs: number;
  endMs: number;
  /** One per day (Monday → Sunday, 1 → last day of the month), or one per
   *  month in a year. */
  ticks: TimelineTick[];
  /** The vertical grid lines: each day's start after the first, or each
   *  month's in a year. */
  gridLines: number[];
  /** "Dec 29 – Jan 4, 2026" / "September 2025" / "2025". */
  label: string;
  containsToday: boolean;
}

const HALF_DAY_MS = 12 * 60 * 60 * 1000;

export const getTimelineWindow = (
  period: TimelinePeriod,
  anchor: Date,
  now: Date = new Date()
): TimelineWindow => {
  const nowMs = now.getTime();
  const within = (from: Date, to: Date) =>
    nowMs >= from.getTime() && nowMs < to.getTime();

  if (period === "year") {
    const start = startOfYear(anchor);
    const end = addYears(start, 1);
    const months = eachMonthOfInterval({ start, end: addMonths(end, -1) });
    return {
      period,
      startMs: start.getTime(),
      endMs: end.getTime(),
      ticks: months.map((month) => ({
        t: (month.getTime() + addMonths(month, 1).getTime()) / 2,
        label: format(month, "MMM ''yy"),
      })),
      gridLines: months.slice(1).map((month) => month.getTime()),
      label: format(start, "yyyy"),
      containsToday: within(start, end),
    };
  }

  const start =
    period === "week"
      ? startOfWeek(anchor, { weekStartsOn: 1 })
      : startOfMonth(anchor);
  const lastDay =
    period === "week"
      ? startOfDay(endOfWeek(anchor, { weekStartsOn: 1 }))
      : startOfDay(endOfMonth(anchor));
  const end = addDays(lastDay, 1);
  const days = eachDayOfInterval({ start, end: lastDay });

  const ticks = days.map((day) => ({
    t: day.getTime() + HALF_DAY_MS,
    label: period === "week" ? format(day, "EEE") : format(day, "d"),
  }));

  const label =
    period === "week"
      ? `${format(start, "MMM d")} – ${format(lastDay, "MMM d, yyyy")}`
      : format(start, "MMMM yyyy");

  return {
    period,
    startMs: start.getTime(),
    endMs: end.getTime(),
    ticks,
    gridLines: days.slice(1).map((day) => day.getTime()),
    label,
    containsToday: within(start, end),
  };
};

/** The anchor one period earlier (-1) or later (+1). */
export const shiftAnchor = (
  period: TimelinePeriod,
  anchor: Date,
  direction: -1 | 1
): Date =>
  period === "week"
    ? addDays(anchor, 7 * direction)
    : period === "month"
    ? addMonths(anchor, direction)
    : addYears(anchor, direction);

/** 0–100 across the window; values outside it fall outside that range. */
export const xPct = (t: number, window: TimelineWindow): number =>
  ((t - window.startMs) / (window.endMs - window.startMs)) * 100;

export const clampPct = (pct: number): number => Math.min(100, Math.max(0, pct));

/**
 * A backend date string → epoch ms, or null. Date-only strings
 * ("2025-09-22") are read as local midnight — `new Date()` would read them
 * as UTC and shift the day west of Greenwich.
 */
export const toTime = (value: string | null | undefined): number | null => {
  if (!value) return null;
  const parsed = parseISO(value);
  return isValid(parsed) ? parsed.getTime() : null;
};

/**
 * A date string → the middle of its day, or null. Readings and tests are
 * plotted per day, so they sit centred in their day's column (between the
 * grid lines) whatever time the record carries — biomarkers are stamped at
 * midnight, which would otherwise put them on the line.
 */
export const toDayCenter = (value: string | null | undefined): number | null => {
  const t = toTime(value);
  return t === null ? null : startOfDay(t).getTime() + HALF_DAY_MS;
};

/** Does `[from, to]` overlap the window at all? */
export const overlapsWindow = (
  from: number,
  to: number,
  window: TimelineWindow
): boolean => to >= window.startMs && from < window.endMs;

/** "Sep 22 2025" — the tooltip date format the design uses. */
export const formatTimelineDate = (t: number): string =>
  format(new Date(t), "MMM d yyyy");
